<template>
  <router-link
    class="preview"
    ref="previewContainer"
    v-waypoint="{ active: true, callback: onWaypoint, options: intersectionOptions}"
    :to="{ name: 'portfolio_page', params: { id: item.id } }"
  >
    <div class="preview__image">
      <div class="preview__imageWrapper">
        <img :src="previewImage" :alt="item.title">
      </div>
    </div>
    <div class="preview__letters">
      <div ref="letters">
        <p class="preview__letter -left">{{ item.letter_first }}</p>
        <p class="preview__letter -right">{{ item.letter_second }}</p>
      </div>
    </div>
    <div class="preview__text">
      <div>
        <h3 class="preview__title" v-html="item.title"></h3>
        <p class="preview__desc">{{ item.pre_text }}</p>
      </div>
    </div>
  </router-link>
</template>

<script>
import { onWaypoint } from 'src/mixins';

import { TweenMax } from 'gsap/all';
import ScrollMagic from 'scrollmagic';
import 'animation.gsap';

export default {
  mixins: [onWaypoint],
  props: {
    item: Object,
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0.25, 0.75],
      },
      scrollMagicController: null,
    };
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
    this.scrollMagicController = new ScrollMagic.Controller();
    new ScrollMagic.Scene({
      triggerElement: this.$refs.previewContainer.$el,
      triggerHook: 1,
      duration: window.innerHeight * 1.5,
    })
      .setTween(TweenMax.from(this.$refs.letters, 0.1, { y: 140 }, { y: -140 }))
      .addTo(this.scrollMagicController);
  },
  computed: {
    previewImage() {
      if (this.windowWidth < 1150 && this.item.preview_bg_mobile) {
        return this.item.preview_bg_mobile;
      }
      return this.item.header_photo
        ? this.item.header_photo
        : this.item.preview_bg;
    },
  },
};
</script>

<style lang="scss">
@import "~styles/vars.scss";

.preview {
  width: 100%;
  display: flex;
  margin-bottom: 170px;
  opacity: 0;
  transition: opacity 0.2s ease-out 0.2s;
  @media only screen and (min-width: 1149px) {
    &:hover{
      .preview__title{
        background-size: 100% 100%;
        background-image: linear-gradient(to right, $dark-gray 0, $dark-gray 100%);
      }
    }
  }
  @media only screen and (max-width : $tablet) {
    flex-direction: column;
    margin-bottom: 130px;
    transition: opacity 0.2s ease-out 0.1s;
  }
  @media only screen and (max-width : $phone) {
    margin-bottom: get-vw(50px);
  }
  &.fade {
    opacity: 1;
    .preview__imageWrapper {
      animation: fadeInLeft 0.6s forwards ease-out;
      animation-delay: 0.1s;

      @media only screen and (max-width: $tablet) {
        animation: none;
      }
    }

    .preview__letters {
      animation: fadeInUp 0.8s forwards ease-out;
      animation-delay: 0.5s;

      @media only screen and (max-width: $tablet) {
        animation: none;
      }
    }

    .preview__text {
      animation: fadeInUp 0.8s forwards ease-out;
      animation-delay: 0.4s;

      @media only screen and (max-width: $tablet) {
        animation: none;
      }
    }
  }

  @media only screen and (min-width: 1151px) {
    &:nth-child(2n){
      flex-direction: row-reverse;
      .preview__image{
        width: 398px;
        img{
          width: 398px;
        }
      }
      .preview__text{
        margin: 0 91px 0 0;
        width: 337px;
      }
      .preview__letter{
        &.-left{
          right: 45px;
        }
        &.-right{
          left: -180px;
        }
      }
    }
  }

  &__image{
    width: 532px;
    height: 450px;
    @media only screen and (max-width : $tablet) {
      width: 100%;
      height: 46.875vw;;
      margin-bottom: 50px;
    }
    @media only screen and (max-width : $phone) {
      height: get-vw(150px);
      margin-bottom: get-vw(23px);
    }
    img{
      object-fit: cover;
      width: 532px;
      height: 100%;
      @media only screen and (max-width : $tablet) {
        width: 100%;
      }
    }
  }

  &__imageWrapper{
    width: 100%;
    height: 100%;
  }

  &__letters{
    height: 0px;
    width: 0px;
    position: relative;
    opacity: 0;
    @media only screen and (max-width : $tablet) {
      display: none;
    }
  }

  &__letter{
    position: absolute;
    font-family: $playfair;
    font-size: 323px;
    font-weight: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: 2.2px;
    color: #ccdedd;
    padding: 0 60px;
    &.-left{
      font-style: italic;
      top: -80px;
      right: -130px;
    }
    &.-right{
      left: 20px;
      top: 50px;
    }
  }

  &__text{
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    z-index: 2;
    margin: 0 0 0 191px;
    opacity: 0;
    padding-left: 15px;
    box-sizing: border-box;
    @media only screen and (max-width : $tablet) {
      opacity: 1;
      width: 634px;
      margin: 0 auto;
    }
    @media only screen and (max-width : $phone) {
      width: 100vw;
      padding: 0 get-vw(14px);
    }
  }

  &__title{
    font-size: 48px;
    line-height: 1.27;
    letter-spacing: 1.7px;
    font-family: $playfair;
    display: inline;
    transition: background 0.5s ease-in-out 0.05s;
    color: $dark-gray;
    background-image: linear-gradient(to right, rgba($dark-gray, 0.9) 0, rgba($dark-gray, 0.4) 100%);
    background-position: 0 63px;
    background-size: 0 100%;
    background-repeat: no-repeat;
    span{
      font-style: italic;
    }
    @media only screen and (max-width : $phone) {
      font-size: get-vw(22px);
      line-height: 1.36;
      letter-spacing: 1.1px;
    }
  }

  &__desc{
    font-family: $circe;
    font-size: 16px;
    font-weight: 300;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: 0.5px;
    text-align: left;
    color: #5f6768;
    margin-top: 26px;
    @media only screen and (max-width : $phone) {
      font-size: get-vw(12px);
      line-height: 1.5;
      letter-spacing: 0.4px;
      margin-top: get-vw(10px);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/portfolio/Preview.vue